import React,{useState, useEffect}  from 'react';
import { Box, Flex, Text, Button, Card, TextField, Select, Badge, TextArea, Separator, Inset } from '@radix-ui/themes';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import aiService from '../../../services/aiService';
import { toast } from 'react-toastify';
import { Cross } from 'recharts';
import { quizService } from '../../../services/quizService';


const validationSchema = Yup.object().shape({
    type: Yup.string().required('Question type is required'),
    text: Yup.string()
        .required('Question text is required'),
    topic_id: Yup.string() // Keep as string for Select, convert to number on submit
        .required('Topic is required'),
    answers: Yup.array()
        .when('type', {
            is: 'multiple_choice',
            then: (schema) => schema
                .of(
                    Yup.object().shape({
                        text: Yup.string().required('Answer text is required'),
                        is_correct: Yup.boolean(),
                    })
                )
                .min(4, 'Must have 4 answers for multiple choice')
                .max(4, 'Must have 4 answers for multiple choice')
                .test('one-correct-answer', 'Exactly one answer must be marked as correct for multiple choice',
                    arr => arr ? arr.filter(ans => ans.is_correct).length === 1 : false),
            otherwise: (schema) => schema.notRequired(),
        }),
    rubric: Yup.object().when('type', {
        is: 'short_answer',
        then: (schema) => schema.shape({
            example_answer: Yup.string().required('Example answer is required'),
            full_credit: Yup.string().required('Full credit criteria is required'),
            partial_credit: Yup.string().required('Partial credit criteria is required'),
            no_credit: Yup.string(),
            // marks: Yup.number().required('Marks are required').positive('Marks must be positive').integer('Marks must be an integer'), // Moved to top level
        }),
        otherwise: (schema) => schema.notRequired(),
        }),
    explanation: Yup.string()
        .required('Explanation is required'),
    core_competencies: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one competency must be selected'),
    difficulty: Yup.string()
        .required('Difficulty is required'),
    time: Yup.number().required('Time allocation is required').positive('Time must be positive').integer('Time must be an integer'),
    marks: Yup.number().required('Marks are required').positive('Marks must be positive').integer('Marks must be an integer'),
});

const EditQuestion = ({ question = {}, onSave, topics, onCancel }) => {
    
    const getInitialAnswers = () => {
        const defaultAnswer = () => ({ text: '', is_correct: false });
        const existingAnswers = question?.answers && Array.isArray(question.answers)
            ? question.answers.map(ans => ({ text: ans.text || '', is_correct: !!ans.is_correct }))
            : [];
        // Ensure exactly 4 answer slots, padding with defaults if necessary
        return Array(4).fill(null).map((_, i) => existingAnswers[i] || defaultAnswer());
    };

    const initialValues = {
        type: question?.type || 'multiple_choice',
        text: question?.text || '',
        difficulty: question?.difficulty || 'medium', // Default difficulty
        core_competencies: question?.core_competencies ? (typeof question.core_competencies === 'string' ? JSON.parse(question.core_competencies) : question.core_competencies) : [],
        explanation: question?.explanation || '',
        topic_id: question?.topic_id ? question.topic_id.toString() : (topics && topics.length > 0 ? topics[0].id.toString() : ''), // Default to first topic if available
        // Answers are initialized regardless of type, but only validated/shown for multiple_choice
        time: question?.time || 60, // Default time in seconds
        marks: question?.marks !== undefined
            ? parseInt(question.marks, 10) // Use existing marks if available
            : ((question?.type || 'multiple_choice') === 'multiple_choice' ? 1 : 5), // Default: 1 for MC, 5 for SA
        answers: getInitialAnswers(),
        rubric: { // Rubric no longer contains marks
            example_answer: question?.rubric?.example_answer || '',
            full_credit: question?.rubric?.full_credit || '',
            partial_credit: question?.rubric?.partial_credit || '',
            no_credit: question?.rubric?.no_credit || '',
        },
    };
    const [generation, setGeneration] = useState({status:'active', count:0});
    const [loading, setLoading] = useState('');

    const competencyOptions = [
        'Vocabulary',
        'Concept',
        'Application'
    ];

    const difficultyOptions = [
        'easy',
        'medium', 
        'hard'
    ];

    const questionTypeOptions = [
        { value: 'multiple_choice', label: 'Multiple Choice' },
        { value: 'short_answer', label: 'Short Answer' },
    ];

    


    return (
        <Flex direction="column" gap="4" width="100%" maxWidth="500px" >
            <Button size="1" color="red" variant="ghost" onClick={onCancel} style={{position:'absolute', right:'10px', top:'10px'}}>
            Discard <Cross2Icon width="15px" height="15px" /> 
            </Button>
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={ async (values) => {
                const submissionData = {
                    ...values,
                    // id: question?.id || null, // Use existing ID if editing, else new
                    topic_id: parseInt(values.topic_id, 10), // Convert topic_id to number
                    // core_competencies: JSON.stringify(values.core_competencies), // Stringify core competencies
                    answers: values.type === 'multiple_choice' ? values.answers : [], 
                    time: parseInt(values.time, 10),
                    marks: parseInt(values.marks, 10), // Marks is now top-level
                    rubric: values.type === 'short_answer'
                        ? values.rubric // Rubric object (without marks)
                        : { example_answer: '', full_credit: '', partial_credit: '', no_credit: '' }, // Default empty rubric for non-SA
                    // Answers structure is already {text: string, is_correct: boolean}
                    // Omit 'correct_answer' as it's embedded in 'answers'
                };
                delete submissionData.correct_answer;
                //save to db
                console.log('submission params', submissionData);
                try{
                    const savedQuestion = await quizService.saveQuestion(submissionData);
                    if(savedQuestion.error){
                        toast.error(savedQuestion.error)
                    } else {
                        toast.success("Question saved successfully!");
                        onSave(savedQuestion);
                    }
                } catch(error){
                    console.error('Error saving question:', error);
                    toast.error("Failed to save question. Please check the console.");
                }
            }}
        >
            {({ values, errors, touched, setFieldValue, handleSubmit }) => {

            const handleAIComplete = async () => {
                setLoading('ai_complete');
                try {
                    const response = await aiService.completeQuestion(
                        {
                            topic_id: values.topic_id,
                            type: values.type,
                            text: values.text
                        }
                    )
                    console.log('completion response', response);
                    // Assuming response.generated is a JSON string
                    const aiData = JSON.parse(response.generated); 

                    if(aiData.error){
                        toast.error(aiData.error);
                        setGeneration(prev => ({ ...prev, status: 'active' })); // Or 'failed'
                    } else {
                        // Update form fields with AI generated data
                        setFieldValue('text', aiData.text || values.text); // Keep existing if AI doesn't provide
                        
                        // Question type might be dictated by AI or kept from user selection
                        // For this example, let's assume AI can suggest a type if not already set,
                        // or we can prioritize user's current selection.
                        // If AI provides type, and it's different, update it.
                        if (aiData.type && aiData.type !== values.type) {
                            setFieldValue('type', aiData.type);
                        }

                        setFieldValue('core_competencies', Array.isArray(aiData.core_competencies) ? aiData.core_competencies : []);
                        setFieldValue('difficulty', aiData.difficulty || 'medium');
                        setFieldValue('explanation', aiData.explanation || '');
                        setFieldValue('time', parseInt(aiData.time, 10) || initialValues.time);
                        
                        let aiMarks = parseInt(aiData.marks, 10);
                        if (isNaN(aiMarks)) {
                            // If AI doesn't provide marks, default based on AI's suggested type or current type
                            const effectiveType = aiData.type || values.type;
                            aiMarks = effectiveType === 'multiple_choice' ? 1 : 5; // Default 5 for SA
                        }
                        setFieldValue('marks', aiMarks);

                        if (values.type === 'multiple_choice' || aiData.type === 'multiple_choice') {
                            let formattedAnswers = Array(4).fill(null).map((_, i) => ({
                                text: aiData.answers?.[i]?.text || '',
                                is_correct: !!aiData.answers?.[i]?.is_correct,
                            }));
                             // Ensure only one correct answer if AI provides multiple, or set first if none
                            const correctAnswersCount = formattedAnswers.filter(a => a.is_correct).length;
                            if (correctAnswersCount > 1) {
                                let firstCorrectFound = false;
                                formattedAnswers = formattedAnswers.map(ans => {
                                    if (ans.is_correct && !firstCorrectFound) {firstCorrectFound = true; return ans;}
                                    return { ...ans, is_correct: false };
                                });
                            } else if (correctAnswersCount === 0 && formattedAnswers.some(a => a.text)) {
                                formattedAnswers[0].is_correct = true;
                            }
                            setFieldValue('answers', formattedAnswers);
                        } else if (values.type === 'short_answer' || aiData.type === 'short_answer') {
                            setFieldValue('rubric.example_answer', aiData.rubric?.example_answer || '');
                            setFieldValue('rubric.full_credit', aiData.rubric?.full_credit || '');
                            setFieldValue('rubric.partial_credit', aiData.rubric?.partial_credit || '');
                            setFieldValue('rubric.no_credit', aiData.rubric?.no_credit || '');
                            // Marks are handled at the top level now
                        }
                        
                        setGeneration(prev => ({ status: 'completed', count: prev.count + 1 }));
                        toast.success("Question details generated successfully!");
                    }
                } catch (error) {
                    console.error('Error completing question:', error);
                    toast.error("Failed to process AI response. Please check the console.");
                    setGeneration(prev => ({ ...prev, status: 'active' })); // Or 'failed'
                } finally {
                    setLoading('');
                }
            }

            return(<Flex direction="column" gap="4">
                <Flex direction="column" gap="2">
                    <Text size="1" weight="bold">Topic</Text>
                    <Select.Root 
                        value={values.topic_id}
                        defaultValue=""
                        onValueChange={value => setFieldValue('topic_id', value)}
                    >
                        <Select.Trigger placeholder="Select a topic" />
                        <Select.Content>
                            {topics?.map(topic => (
                                <Select.Item key={topic.id} value={topic.id.toString()}>
                                    {topic.title}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select.Root>
                    
                    {errors.topic_id && touched.topic_id && (
                        <Text size="1" color="red">{errors.topic_id}</Text>
                    )}
                </Flex>

                <Inset side="x">
                    <Separator size="4" />
                </Inset>

                <Flex direction="column" gap="2">
                    <Text size="1" weight="bold">Question Type</Text>
                    <Flex gap="2">
                        <Button size="1" onClick={() => {
                            setFieldValue('type', 'multiple_choice');
                            setFieldValue('marks', 1); // Default marks for MC
                        }} variant={values.type === 'multiple_choice' ? "solid" : "soft"}>Multi Choice</Button>
                        <Button size="1" onClick={() => {
                            setFieldValue('type', 'short_answer');
                            setFieldValue('marks', 5); // Default marks for SA
                        }} variant={values.type === 'short_answer' ? "solid" : "soft"}>Short Answer</Button>
                    </Flex>
                    {errors.type && touched.type && <Text size="1" color="red">{errors.type}</Text>}
                </Flex>

                <Inset side="x">
                    <Separator size="4" />
                </Inset>

                <Flex direction="column" gap="2">
                    <Text size="1" weight="bold">Question Text</Text>
                    <Box style={{position:'relative'}}>
                        <Field
                            as={TextArea}
                            name="text"
                            placeholder="Enter question text"
                            style={{ minHeight: '200px' }}
                        />
                        <Button size="1" loading={loading === 'ai_complete'} variant="solid" onClick={() => handleAIComplete()} color="orange" style={{position:'absolute', right:'10px', bottom:'10px'}}>AI Complete</Button>
                    </Box>
                    {errors.text && touched.text && (
                        <Text color="red" size="1">{errors.text}</Text>
                    )}
                </Flex>

                

                {(generation.status === 'completed' || generation.status === 'canceled') ? (
                    <>
                <Inset side="x">
                    <Separator size="4" />
                </Inset>
                
                <Flex direction="column" gap="2">
                    <Text size="1" weight="bold">Core Competencies</Text>
                    <Flex gap="2" wrap="wrap">
                        {competencyOptions.map(competency => (
                            <Badge
                                key={competency}
                                variant={values.core_competencies.includes(competency.toLowerCase()) ? "solid" : "soft"}
                                onClick={() => {
                                    const competencies = [...values.core_competencies];
                                    const comp = competency.toLowerCase();
                                    const index = competencies.indexOf(comp);
                                    
                                    if (index === -1) {
                                        competencies.push(comp);
                                    } else {
                                        competencies.splice(index, 1);
                                    }
                                    setFieldValue('core_competencies', competencies);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                {competency}
                            </Badge>
                        ))}
                    </Flex>
                    {errors.core_competencies && touched.core_competencies && (
                        <Text color="red" size="1">{errors.core_competencies}</Text>
                    )}
                </Flex>
                <Inset side="x">
                    <Separator size="4" />
                </Inset>
                <Flex direction="row" justify="start" align="center" gap="2">
                    <Text size="1" weight="bold">Time (seconds)</Text>
                    <Field
                        as={TextField.Input}
                        type="number"
                        name="time"
                        placeholder="e.g., 60"
                        style={{maxWidth:'100px'}}
                    />
                    {errors.time && touched.time && (
                        <Text color="red" size="1">{errors.time}</Text>
                    )}
                </Flex>
                <Inset side="x">
                    <Separator size="4" />
                </Inset>
                <Flex direction="row" justify="start" align="center" gap="2">
                    <Text size="1" weight="bold">Marks</Text>
                    <Field
                        as={TextField.Input}
                        type="number"
                        name="marks"
                        placeholder="e.g., 5"
                        style={{maxWidth:'100px'}}
                    />
                    {errors.marks && touched.marks && (
                        <Text color="red" size="1">{errors.marks}</Text>
                    )}
                </Flex>
                <Inset side="x">
                    <Separator size="4" />
                </Inset>
                <Flex direction="row" justify="start" align="center" gap="2">
                    <Text size="1" weight="bold">Difficulty</Text>
                    <Select.Root 
                        value={values.difficulty}
                        onValueChange={value => setFieldValue('difficulty', value)}
                    >
                        <Select.Trigger />
                        <Select.Content>
                            {difficultyOptions.map(difficulty => (
                                <Select.Item key={difficulty} value={difficulty}>
                                    {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select.Root>
                    {errors.difficulty && touched.difficulty && (
                        <Text color="red" size="1">{errors.difficulty}</Text>
                    )}
                </Flex>
                <Inset side="x">
                    <Separator size="4" />
                </Inset>

                {values.type === 'multiple_choice' && (
                    <>
                        <Flex direction="column" gap="2">
                            <Text size="1" weight="bold">Answer Options</Text>
                            {values.answers.map((answer, index) => (
                                <Flex key={index} gap="2" align="center">
                                    <input
                                        type="radio"
                                        name={`answers_radio_group`} // A common name for the radio group
                                        checked={values.answers[index].is_correct}
                                        onChange={() => {
                                            const newAnswers = values.answers.map((ans, ansIdx) => ({
                                                ...ans,
                                                is_correct: ansIdx === index
                                            }));
                                            setFieldValue('answers', newAnswers);
                                        }}
                                    />
                                    <Field
                                        name={`answers[${index}].text`}
                                        as={TextField.Input}
                                        placeholder={`Answer ${index + 1}`}
                                        style={{fontSize:'12px', flex:1}}
                                    />
                                </Flex>
                            ))}
                            {errors.answers && touched.answers && typeof errors.answers === 'string' && (
                                <Text color="red" size="1">{errors.answers}</Text>
                            )}
                        </Flex>
                        <Inset side="x">
                            <Separator size="4" />
                        </Inset>
                    </>
                )}
                

                {values.type === 'short_answer' && (
                    <>
                        <Flex direction="column" gap="2">
                            <Text size="1" weight="bold">Rubric</Text>
                            <Flex direction="column" gap="1">
                                <Text size="1" color="gray">Example Answer</Text>
                                <Field
                                    as={TextArea}
                                    name="rubric.example_answer"
                                    placeholder="Provide an ideal example answer"
                                />
                                {errors.rubric?.example_answer && touched.rubric?.example_answer && (
                                    <Text color="red" size="1">{errors.rubric.example_answer}</Text>
                                )}
                            </Flex>
                            <Flex direction="column" gap="1">
                                <Text size="1" color="gray">Full Credit Criteria</Text>
                                <Field
                                    as={TextArea}
                                    name="rubric.full_credit"
                                    placeholder="Criteria for full credit"
                                />
                                {errors.rubric?.full_credit && touched.rubric?.full_credit && (
                                    <Text color="red" size="1">{errors.rubric.full_credit}</Text>
                                )}
                            </Flex>
                            <Flex direction="column" gap="1">
                                <Text size="1" color="gray">Partial Credit Criteria</Text>
                                <Field
                                    as={TextArea}
                                    name="rubric.partial_credit"
                                    placeholder="Criteria for partial credit"
                                />
                                {errors.rubric?.partial_credit && touched.rubric?.partial_credit && (
                                    <Text color="red" size="1">{errors.rubric.partial_credit}</Text>
                                )}
                            </Flex>
                            <Flex direction="column" gap="1">
                                <Text size="1" color="gray">No Credit Criteria</Text>
                                <Field
                                    as={TextArea}
                                    name="rubric.no_credit"
                                    placeholder="Criteria for no credit"
                                />
                                {errors.rubric?.no_credit && touched.rubric?.no_credit && (
                                    <Text color="red" size="1">{errors.rubric.no_credit}</Text>
                                )}
                            </Flex>
                        </Flex>
                        <Inset side="x"><Separator size="4" /></Inset>
                    </>
                )}

                <Flex direction="column" gap="2">
                    <Text size="1" weight="bold">Explanation</Text>
                    <Field
                        as={TextArea}
                        name="explanation"
                        placeholder="Enter explanation"
                    />
                    {errors.explanation && touched.explanation && (
                        <Text color="red" size="1">{errors.explanation}</Text>
                    )}
                </Flex>
                <Inset side="x">
                    <Separator size="4" />
                </Inset>

                <Flex justify="end" gap="2">
                    <Button variant="soft" color="gray" type="button" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="solid" type="submit" onClick={handleSubmit}>
                        Save Question
                    </Button>
                </Flex>
                </>):(
                    <Flex direction="row" gap="2" justify="between">
                        <Button onClick={() => setGeneration({...generation, status:'canceled'})}>Continue</Button>
                        
                    </Flex>
                )}

            </Flex>);
            }}
        </Formik>
    </Flex>
    );
};

export default EditQuestion;
